import React from 'react';
import { styled } from 'styled-components';
import { Button } from '../../components/button/Button';
import { Heading } from '../../components/typography/Heading';
import { SubHeading } from '../../components/typography/SubHeading';
import { gradientText } from '../../components/utils/gradientText';
import { contactInfo } from '../../config/navigation';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 0;
  background-color: white;
  width: 100%;
`;

const ContentWrapper = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  width: 100%;
  padding: 0 ${({ theme }) => theme.spacing.md};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 1rem;
    width: revert;
  }
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 4rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 2.25rem;
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  }
`;

const StepsSection = styled.div``;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 0;
  }
`;

const Step = styled.div<{ isLast?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  z-index: 2;

  ${({ isLast }) =>
    !isLast &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 1.5rem;
      left: calc(100% - 18rem);
      width: calc(100% - 2rem);
      height: 2px;
      background-image: linear-gradient(to right, black 10px, transparent 10px, transparent 30px);
      background-size: 20px 100%;
      background-repeat: repeat-x;
      z-index: 1;
    }
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    &::after {
      display: none;
    }
  }
`;

const StepNumber = styled.span`
  ${gradientText}
  font-size: ${({ theme }) => theme.typography.fontSizes.xxl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0;
    margin: 0;
  }
`;

const StepTitle = styled.h3`
  ${gradientText}
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0.5rem 0;
  }
`;

const StepDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 90%;
    margin: 0.5rem 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: left;
    margin-top: ${({ theme }) => theme.spacing.md};
  }
`;

export const HowItWorks: React.FC = () => {
  const steps = [
    {
      number: '01',
      title: 'Introductory call and initial meeting',
      description: 'Offer a portfolio scan, discuss goal setting, risk assessment, and strategy development.',
    },
    {
      number: '02',
      title: 'Creating a plan and its implementation',
      description: 'Execute planned solutions based on selected funds and transaction setups.',
    },
    {
      number: '03',
      title: 'Ongoing support',
      description: 'Regular evaluation and follow-up to ensure alignment with financial objectives and goals.',
    },
  ];

  const handleClick = () => {
    // Track metrics
    console.log("Let's connect button clicked from HowItWorks section");

    // Open mailto link
    window.location.href = `https://wa.me/${contactInfo.phoneNumber}`;
  };

  return (
    <Container>
      <ContentWrapper>
        <StepsSection>
          <SubHeading>HOW IT WORKS</SubHeading>
          <StyledHeading>
            From the beginning, we&apos;re in this <em>together</em>.
          </StyledHeading>
          <StepsContainer>
            {steps.map((step, index) => (
              <Step key={step.number} isLast={index === steps.length - 1}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Step>
            ))}
          </StepsContainer>
        </StepsSection>
        <ButtonContainer>
          <Button onClick={handleClick} size="large">
            Let&apos;s connect
          </Button>
        </ButtonContainer>
      </ContentWrapper>
    </Container>
  );
};
